import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, User, Building, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import { API } from "../App";
import { loadMetaPixel, trackPixel } from "../utils/metaPixel";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ name: "", email: "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const [selectedPlanInfo, setSelectedPlanInfo] = useState(null);

  const selectedPlanId = location.state?.selectedPlan || null;

  useEffect(() => {
    loadMetaPixel();
    // Fire Lead the moment someone lands on /register — captures every entry
    // path (Hero CTA, Pricing card, Nav, Mobile sticky, etc.) in one place.
    trackPixel("Lead", { content_name: "register_page_viewed" });
    // StartTrial — the 7-day free trial begins the moment a visitor reaches
    // the registration page (whether or not they complete the form). Fired
    // alongside Lead so Meta Ads campaigns optimized on StartTrial conversions
    // count every funnel entry.
    trackPixel("StartTrial");
  }, []);

  useEffect(() => {
    if (!selectedPlanId) return;
    axios.get(`${API}/plans`)
      .then(res => {
        const found = (res.data.plans || []).find(p => p.plan_id === selectedPlanId);
        if (found) setSelectedPlanInfo(found);
      })
      .catch(() => {});
  }, [selectedPlanId]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      localStorage.setItem("token", response.data.token);
      if (response.data.refresh_token) {
        localStorage.setItem("refresh_token", response.data.refresh_token);
      }
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("auth_kind", "owner"); // marker for silent-refresh interceptor
      trackPixel("CompleteRegistration", {
        content_name: "owner_signup",
        status: true,
        value: 0,
        currency: "USD",
      });
      toast.success("Account created successfully!");
      if (selectedPlanId) {
        localStorage.setItem("pending_plan_selection", JSON.stringify({
          plan_id: selectedPlanId,
          at: Date.now(),
        }));
        navigate("/dashboard/settings?tab=my-plan");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lux">
      <div className="lux-auth-shell">
        <div className="lux-auth-card" data-testid="register-card">
          <Link to="/" className="flex items-center justify-center mb-7">
            <img src="/logo.png" alt="Fieldz AI" className="h-11 object-contain" />
          </Link>

          <div className="text-center mb-7">
            <h1 className="text-[26px] font-bold text-[var(--lux-text)] mb-1.5" style={{ letterSpacing: "-0.02em" }}>
              Start your <span className="lux-gold-text">free trial</span>
            </h1>
            <p className="lux-body text-[14px]">7 days free. No credit card required.</p>
          </div>

          {selectedPlanInfo && (
            <div
              className="rounded-xl p-3.5 flex items-center gap-3 mb-5"
              style={{ background: "rgba(34, 197, 94, 0.08)", border: "1px solid rgba(34, 197, 94, 0.30)" }}
              data-testid="selected-plan-banner"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(34, 197, 94, 0.18)" }}>
                <Check className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.1em] font-semibold" style={{ color: "var(--lux-text-3)" }}>Selected plan</p>
                <p className="text-[14px] font-semibold text-[var(--lux-text)] truncate">
                  {selectedPlanInfo.name}
                  {!selectedPlanInfo.is_contact_us && selectedPlanInfo.price > 0 && (
                    <span className="ml-2 text-emerald-400">${selectedPlanInfo.price}/mo</span>
                  )}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="lux-label">Full Name</label>
              <div className="relative">
                <User className="lux-input-icon" />
                <input
                  id="name" name="name" type="text" placeholder="John Doe"
                  value={formData.name} onChange={handleChange}
                  className="lux-input has-icon" required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="lux-label">Email</label>
              <div className="relative">
                <Mail className="lux-input-icon" />
                <input
                  id="email" name="email" type="email" placeholder="you@company.com"
                  value={formData.email} onChange={handleChange}
                  className="lux-input has-icon" required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company_name" className="lux-label">Company Name (Optional)</label>
              <div className="relative">
                <Building className="lux-input-icon" />
                <input
                  id="company_name" name="company_name" type="text" placeholder="Your Business Name"
                  value={formData.company_name} onChange={handleChange}
                  className="lux-input has-icon"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="lux-label">Password</label>
              <div className="relative">
                <Lock className="lux-input-icon" />
                <input
                  id="password" name="password" type="password" placeholder="••••••••"
                  value={formData.password} onChange={handleChange}
                  className="lux-input has-icon" required minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <LuxButton type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating Account..." : "Create Account"} {!loading && <ArrowRight className="w-4 h-4" />}
            </LuxButton>
          </form>

          <p className="text-center text-[12px] mt-5" style={{ color: "var(--lux-text-3)" }}>
            No credit card required. Cancel anytime.
          </p>

          <div className="mt-5 pt-5 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <p className="text-center text-[14px]" style={{ color: "var(--lux-text-2)" }}>
              Already have an account?{" "}
              <Link to="/login" className="lux-link-gold" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
